import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router'

import {
  URL_BLOG,
  URL_CART,
  URL_CHECKOUT,
  URL_CUSTOMER_ADDRESSES,
  URL_CUSTOMER_FAVORITES,
  URL_CUSTOMER_LOYALTY,
  URL_CUSTOMER_OFFLINE_ORDERS,
  URL_CUSTOMER_ORDERS,
  URL_CUSTOMER_PAYMENT,
  URL_CUSTOMER_PROFILE,
  URL_MAIN_PAGE,
  URL_PAGE_ABOUT,
  URL_PAGE_CONTACTS,
  URL_PAGE_DELIVERY_INFO,
  URL_PAGE_PAYMENT_INFO,
  URL_SEARCH,
} from '@shared/contants'
import { BRAND_NAME } from '@shared/contants/app'
import { MetaData, setPageMetadata } from '@shared/lib/seo/seo-utils'

const BRAND = BRAND_NAME

const defaultsForPath = (pathname: string): MetaData => {
  // Базовые мета-данные для всех страниц
  const common: Partial<MetaData> = {
    ogSiteName: BRAND,
  }

  // Функция для проверки начала строки
  const startsWithPath = (path: string) => pathname.startsWith(path)
  
  // Стратегии определения мета-данных
  const metaStrategies = [
    // Private or volatile pages: noindex
    {
      condition: () => 
        startsWithPath(URL_CART) ||
        startsWithPath(URL_CHECKOUT) ||
        startsWithPath(URL_SEARCH),
      meta: {
        ...common,
        title: 'Служебная страница',
        description: 'Техническая страница магазина',
        noindex: true,
      }
    },
    // Main profile page
    {
      condition: () => pathname === URL_CUSTOMER_PROFILE,
      meta: {
        ...common,
        title: `Личный кабинет | ${BRAND}`,
        description: `Управление персональными данными и настройками аккаунта в ${BRAND}.`,
      }
    },
    // Loyalty program page
    {
      condition: () => pathname === `${URL_CUSTOMER_PROFILE}/${URL_CUSTOMER_LOYALTY}`,
      meta: {
        ...common,
        title: `Бонусы и скидки | ${BRAND}`,
        description: `Ваши бонусы, история начислений и купоны в программе лояльности ${BRAND}.`,
      }
    },
    // Online orders page
    {
      condition: () => pathname === `${URL_CUSTOMER_PROFILE}/${URL_CUSTOMER_ORDERS}`,
      meta: {
        ...common,
        title: `Мои заказы | ${BRAND}`,
        description: `История онлайн-заказов в ${BRAND} и статус текущих доставок.`,
      }
    },
    // Offline orders page
    {
      condition: () => pathname === `${URL_CUSTOMER_PROFILE}/${URL_CUSTOMER_OFFLINE_ORDERS}`,
      meta: {
        ...common,
        title: `Офлайн-покупки | ${BRAND}`,
        description: `История покупок в розничных магазинах ${BRAND}.`,
      }
    },
    // Favorites page
    {
      condition: () => pathname === `${URL_CUSTOMER_PROFILE}/${URL_CUSTOMER_FAVORITES}`,
      meta: {
        ...common,
        title: `Избранное | ${BRAND}`,
        description: `Сохраненные товары и избранные продукты в ${BRAND}.`,
      }
    },
    // Addresses page
    {
      condition: () => pathname === `${URL_CUSTOMER_PROFILE}/${URL_CUSTOMER_ADDRESSES}`,
      meta: {
        ...common,
        title: `Адреса доставки | ${BRAND}`,
        description: `Управление адресами доставки в личном кабинете ${BRAND}.`,
      }
    },
    // Payment methods page
    {
      condition: () => pathname === `${URL_CUSTOMER_PROFILE}/${URL_CUSTOMER_PAYMENT}`,
      meta: {
        ...common,
        title: `Способы оплаты | ${BRAND}`,
        description: `Управление способами оплаты в личном кабинете ${BRAND}.`,
      }
    },
    // Home page
    {
      condition: () => pathname === URL_MAIN_PAGE,
      meta: {
        ...common,
        title: `${BRAND} — доставка продуктов на дом`,
        description: 'Онлайн-супермаркет: свежие продукты, акции и быстрая доставка.',
      }
    },
    // About page
    {
      condition: () => pathname === URL_PAGE_ABOUT,
      meta: {
        ...common,
        title: `О компании | ${BRAND}`,
        description: 'История, миссия и контакты компании Анвар.',
      }
    },
    // Contacts page
    {
      condition: () => pathname === URL_PAGE_CONTACTS,
      meta: {
        ...common,
        title: `Контакты | ${BRAND}`,
        description: 'Адреса, телефоны и способы связи с Анваром.',
      }
    },
    // Delivery page
    {
      condition: () => pathname === URL_PAGE_DELIVERY_INFO,
      meta: {
        ...common,
        title: `Доставка | ${BRAND}`,
        description: 'Условия и стоимость доставки заказов.',
      }
    },
    // Payment page
    {
      condition: () => pathname === URL_PAGE_PAYMENT_INFO,
      meta: {
        ...common,
        title: `Оплата | ${BRAND}`,
        description: 'Способы оплаты заказов в торговой сети Анвар.',
      }
    },
    // Blog pages
    {
      condition: () => startsWithPath(URL_BLOG),
      meta: {
        ...common,
        title: `Блог | ${BRAND}`,
        description: 'Советы, новости и подборки от Анвара.',
      }
    },
  ]

  // Находим первую подходящую стратегию или используем fallback
  const matchingStrategy = metaStrategies.find(strategy => strategy.condition())
  
  if (matchingStrategy) {
    return matchingStrategy.meta as MetaData
  }
  
  // Fallback
  return {
    ...common,
    title: `${BRAND}`,
    description: 'Интернет-магазин продуктов с быстрой доставкой.',
  } as MetaData
}

export const RouteSEO = () => {
  const { pathname } = useLocation()
  const { t } = useTranslation('seo')

  useEffect(() => {
    const meta = defaultsForPath(pathname)
    // Canonical: compute from current location; og:url handled inside setPageMetadata
    meta.canonicalUrl = pathname

    // Функция для проверки начала строки (для блога и других разделов)
    const startsWithPath = (path: string) => pathname.startsWith(path)
    
    // Полностью объектный подход с приоритетной проверкой
    const pageKeyStrategies = [
      { condition: () => meta.noindex, key: 'private' },
      { condition: () => pathname === URL_MAIN_PAGE, key: 'home' },
      { condition: () => pathname === URL_PAGE_ABOUT, key: 'about' },
      { condition: () => pathname === URL_PAGE_CONTACTS, key: 'contacts' },
      { condition: () => pathname === URL_PAGE_DELIVERY_INFO, key: 'delivery' },
      { condition: () => pathname === URL_PAGE_PAYMENT_INFO, key: 'payment' },
      { condition: () => startsWithPath(URL_BLOG), key: 'blog' },
      { condition: () => pathname === URL_CUSTOMER_PROFILE, key: 'profile' },
      { condition: () => pathname === `${URL_CUSTOMER_PROFILE}/${URL_CUSTOMER_LOYALTY}`, key: 'loyalty' },
      { condition: () => pathname === `${URL_CUSTOMER_PROFILE}/${URL_CUSTOMER_ORDERS}`, key: 'orders' },
      { condition: () => pathname === `${URL_CUSTOMER_PROFILE}/${URL_CUSTOMER_OFFLINE_ORDERS}`, key: 'offlineOrders' },
      { condition: () => pathname === `${URL_CUSTOMER_PROFILE}/${URL_CUSTOMER_FAVORITES}`, key: 'favorites' },
      { condition: () => pathname === `${URL_CUSTOMER_PROFILE}/${URL_CUSTOMER_ADDRESSES}`, key: 'addresses' },
      { condition: () => pathname === `${URL_CUSTOMER_PROFILE}/${URL_CUSTOMER_PAYMENT}`, key: 'payment_methods' }
    ]
    
    // Находим первый подходящий ключ или используем default
    const pageKey = pageKeyStrategies.find(strategy => strategy.condition())?.key || 'default'
    
    // Применяем локализованные переводы, если они существуют
    if (pageKey !== 'default') {
      meta.title = t(`${pageKey}.title`, { brand: BRAND })
      meta.description = t(`${pageKey}.description`, { brand: BRAND })
    }

    setPageMetadata(meta)
  }, [ pathname, t ])

  return null
}

export default RouteSEO
